// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { ID3EPolicy } from '../../../../types/d3ePolicy';
import { MatSliderThumb } from '@angular/material/slider';
import { MFA, MfaOptions } from '../../../../types/subscription';

const getValue = (value: any, defaultValue: any): any => {
  return typeof value === 'undefined' ? defaultValue : value;
};

@Component({
    selector: 'app-fde-settings',
    templateUrl: './fde-settings.component.html',
    styleUrl: './fde-settings.component.scss',
    standalone: false
})
export class FdeSettingsComponent implements OnChanges {
  @ViewChild('ngSliderThumbLock', {static: false}) matSliderLock: MatSliderThumb;
  @ViewChild('ngSliderThumbErase', {static: false}) matSliderErase: MatSliderThumb;
  @ViewChild('ngSliderThumbHistory', {static: false}) matSliderHistory: MatSliderThumb;
  @ViewChild('ngSliderThumbMinimum', {static: false}) matSliderMinimum: MatSliderThumb;
  @Input() d3ePolicy: ID3EPolicy;
  @Output() onChanged:  EventEmitter<void> = new EventEmitter<void>();

  formGroup: UntypedFormGroup;
  readonly failedLoginsLockoutMin = 1;
  readonly failedLoginsLockoutMax = 9;
  readonly failedLoginsLockoutDefault = 5;
  readonly failedLoginsEraseMin = 0;
  readonly failedLoginsEraseMax = 999;
  readonly failedLoginsEraseDefault = 0;
  readonly passwordHistoryMin = 1;
  readonly passwordHistoryMax = 20;
  readonly passwordHistoryDefault = 1;
  readonly passwordMinimumMin = 8;
  readonly passwordMinimumMax = 128;
  readonly passwordMinimumDefault = 8;

  protected readonly MFA = MFA;
  protected readonly MfaOptions = MfaOptions;

  inputLabelStyles = {
    'min-width': '68px',
    'display': 'inline-block',
    'text-align': 'left',
  };
  inputAttemptsLabelStyles = {
    'min-width': '64px',
    'display': 'inline-block',
    'text-align': 'left',
  }
  minLabelStyles = {
    'min-width': '15px',
  };
  maxLabelStyles = {
    'min-width': '22px',
    'text-align': 'right'
  };
  inputStyles = {
    'min-width': '44px'
  };

  constructor(private formBuilder: FormBuilder) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const firstChange = changes['d3ePolicy']?.firstChange;
    const hasChanged =  firstChange || JSON.stringify(changes['d3ePolicy']?.currentValue?.fdeSettings) !== JSON.stringify(changes['d3ePolicy']?.previousValue?.fdeSettings);
    if (hasChanged) {
      this.formGroup = this.formBuilder.group({
        // FDE Settings
        failedLoginsBeforeLockout: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.failedLoginsBeforeLockout, this.failedLoginsLockoutDefault),
        failedLoginsBeforeErase: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.failedLoginsBeforeErase, this.failedLoginsEraseDefault),
        passwordHistory: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.passwordHistory, this.passwordHistoryDefault),
        passwordMinimum: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.passwordMin, this.passwordMinimumDefault),
        mfa: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.mfa, MFA.NotRequired),
        mfaOptions: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.mfaOptions, MfaOptions.None),
        enableRememberMe: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.enableRememberMe, false),
        allowTPM: getValue(changes['d3ePolicy'].currentValue?.fdeSettings?.allowTPM, false),
      });
    }
  }

  onPasswordLockInputChanged(value) {
    if (this.matSliderLock && this.matSliderLock.value !== value) {
      this.matSliderLock.value = value;
      this.onDataChanged();
    }
  }

  onPasswordEraseInputChanged(value) {
    if (this.matSliderErase && this.matSliderErase.value !== value) {
      this.matSliderErase.value = value;
      this.onDataChanged();
    }
  }

  onPasswordHistoryChanged(value) {
    if (this.matSliderHistory && this.matSliderHistory.value !== value) {
      this.matSliderHistory.value = value;
      this.onDataChanged();
    }
  }

  onPasswordMinimumChanged(value) {
    if (this.matSliderMinimum && this.matSliderMinimum.value !== value) {
      this.matSliderMinimum.value = value;
      this.onDataChanged();
    }
  }

  onDataChanged() {
    this.d3ePolicy.fdeSettings = {
      failedLoginsBeforeLockout: this.matSliderLock.value,
      failedLoginsBeforeErase: this.matSliderErase.value,
      passwordHistory: this.matSliderHistory.value,
      passwordMin: this.matSliderMinimum.value,
      enableRememberMe: this.formGroup.value.enableRememberMe,
    }
    this.onChanged.emit();
  }

}